import { Stack, useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { DateRange } from "react-big-calendar";

import { ColorHelper } from "@/common/helpers/color";
import { DatetimeHelper } from "@/common/helpers/datetime";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { Mapper } from "@/common/mapping/mapper";
import { GenericCalendarEntryOfScheduleEntryDto } from "@/common/mapping/mappers/scheduleEntry";
import { apiClient } from "@/core/api/ApiClient";
import { VehicleDto } from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import AppIcon from "../../Icons/AppIcon";
import AppPopover from "../../Popover/AppPopover";
import AppPopoverContent from "../../Popover/AppPopoverContent";
import { Calendar, CalendarProps } from "../Calendar";
import { CalendarEntryPopoverContent } from "../CalendarEntryPopoverContent";
import { CalendarExternalToolbar } from "../CalendarExternalToolbar";
import { ScheduleEntryDetails } from "../ScheduleEntryDetails";
import { ScheduleEntrySourceEntityInline } from "../ScheduleEntrySourceEntityInline";
import { VehicleScheduleEntryCreateDefaultValues } from "./VehicleScheduleEntryCreate";
import { VehicleScheduleEntryCreateModal } from "./VehicleScheduleEntryCreateModal";
import {
  VehicleScheduleEntryFilters,
  VehicleScheduleEntryFiltersInput,
} from "./VehicleScheduleEntryFiltersInput";

interface Props {
  vehicle: VehicleDto;
  tabs: ReactNode;
  calendarProps: Partial<CalendarProps<GenericCalendarEntryOfScheduleEntryDto>>;
}

export function VehicleActivityCalendar({ vehicle, tabs, calendarProps }: Props) {
  const theme = useTheme();

  const [dateRange, setDateRange] = useState<DateRange>(() =>
    DatetimeHelper.getMonthRangeExclusive(calendarProps.defaultDate),
  );
  const [filters, setFilters] = useState<VehicleScheduleEntryFilters | null>(null);
  const [entryCreateDefaultValues, setEntryCreateDefaultValues] =
    useState<Partial<VehicleScheduleEntryCreateDefaultValues> | null>(null);
  const [isEntryCreateModalOpen, setIsEntryCreateModalOpen] = useState(false);

  const entriesRequest = useApiRequest(
    apiClient.vehicleScheduleApi.apiV1VehiclesVehicleIdScheduleEntriesGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: vehicle.id || "",
      scheduleEntriesGetDto: {
        range: {
          from: dateRange.start.toISOString(),
          to: dateRange.end.toISOString(),
        },
        entityTypes: filters?.entityTypes,
      },
    },
    {
      deps: [vehicle.id, dateRange, filters],
    },
  );
  const entries = entriesRequest.data?.entries;

  const genericEntries = useMemo(
    () =>
      entries?.map((x) => {
        const colorSet = ColorHelper.getAppSoftColorSetForString({
          str: x.sourceEntity?.entityType?.toString() || "",
          isDarkTheme: theme.palette.mode === "dark",
          alphaDark: 1,
          alphaLight: 1,
        });

        return Mapper.scheduleEntry.mapScheduleEntryDtoToGenericCalendarEntry(x, {
          style: {
            backgroundColor: colorSet.backgroundColorHsla.hsla,
            color: colorSet.textColorHsla.hsla,
          },
        });
      }) || [],
    [entries, theme.palette.mode],
  );

  return (
    <Stack gap={1}>
      <CalendarExternalToolbar
        isLoading={entriesRequest.isLoading}
        tabs={tabs}
        actions={
          <>
            <AppPopover
              clickBehavior={{}}
              trigger={
                <AppIconButton
                  tooltipProps={{ title: "Filters", enterDelay: 300, enterNextDelay: 300 }}
                >
                  <AppIcon of='filters' />
                </AppIconButton>
              }
            >
              {(params) => (
                <AppPopoverContent sx={{ width: { xxs: 250, md: 300, lg: 400 } }}>
                  <VehicleScheduleEntryFiltersInput
                    defaultValue={filters}
                    onCancel={params.close}
                    onSave={(value) => {
                      setFilters(value);
                      params.close();
                    }}
                  />
                </AppPopoverContent>
              )}
            </AppPopover>
          </>
        }
        onRefetch={entriesRequest.refetch}
        onCreateEntry={() => {
          setEntryCreateDefaultValues({
            dateRange: {
              start: new Date(),
              end: undefined,
            },
          });
          setIsEntryCreateModalOpen(true);
        }}
      />

      <Calendar
        {...calendarProps}
        entries={genericEntries}
        renderEntryTitle={(genericEntry) => (
          <ScheduleEntrySourceEntityInline entry={genericEntry.data} />
        )}
        renderEntryPopoverContent={(params) => (
          <CalendarEntryPopoverContent sx={{ minWidth: { xxs: 200, md: 400 } }}>
            <ScheduleEntryDetails
              entry={params.entry.data}
              onSourceEntityUpdate={() => {
                entriesRequest.refetch();
              }}
              onSourceEntityDelete={() => {
                params.close();
                entriesRequest.refetch();
              }}
            />
          </CalendarEntryPopoverContent>
        )}
        onDateRangeChange={(newValue) => {
          setDateRange(newValue.dateRangeExclusive);
        }}
        onSlotClick={(newValue) => {
          setEntryCreateDefaultValues({
            vehicleId: vehicle.id,
            vehicle: vehicle,
            dateRange: newValue.range,
          });
          setIsEntryCreateModalOpen(true);
        }}
      />

      {entryCreateDefaultValues && (
        <VehicleScheduleEntryCreateModal
          open={isEntryCreateModalOpen}
          onClose={() => {
            setIsEntryCreateModalOpen(false);
            setEntryCreateDefaultValues(null);
          }}
          createProps={{
            defaultValues: {
              ...entryCreateDefaultValues,
              vehicleId: vehicle.id,
              vehicle: vehicle,
              dateRange: entryCreateDefaultValues.dateRange || {
                start: new Date(),
                end: undefined,
              },
            },
            onCancel: () => {
              setIsEntryCreateModalOpen(false);
              setEntryCreateDefaultValues(null);
            },
            onSave: () => {
              entriesRequest.refetch();
              setIsEntryCreateModalOpen(false);
              setEntryCreateDefaultValues(null);
            },
          }}
        />
      )}
    </Stack>
  );
}
